<template>
  <div class="accesos-personas">
    <div class="chart-card">
      <!-- Cabecera: total + selector de límite -->
      <div class="personas-toolbar">
        <div class="personas-total">
          <i class="fas fa-users"></i>
          <span v-if="!loading">
            <strong>{{ fmt(totalRecords) }}</strong> persona{{ totalRecords === 1 ? '' : 's' }}
          </span>
          <span v-else class="spinner-border spinner-border-sm text-success" role="status"></span>
        </div>
        <div class="personas-limit">
          <label class="me-2 mb-0">Mostrar</label>
          <select class="form-select form-select-sm" v-model.number="limit" @change="onLimitChange">
            <option v-for="opt in limitOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>
      </div>

      <!-- Tabla -->
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0 personas-table">
          <thead>
            <tr>
              <th
                v-for="col in columns"
                :key="col.key"
                :class="[col.thClass, { sortable: col.sortField }]"
                @click="col.sortField && toggleSort(col.sortField)"
              >
                {{ col.label }}
                <i v-if="col.sortField" :class="sortIcon(col.sortField)" class="sort-icon"></i>
              </th>
              <th class="text-end">Detalle</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td :colspan="columns.length + 1" class="text-center py-5">
                <div class="spinner-border text-success" role="status"></div>
              </td>
            </tr>
            <template v-else>
              <tr v-for="p in records" :key="p.rutSocio">
                <td>
                  <div class="fw-semibold">{{ p.nombrePersona }}</div>
                  <div class="d-md-none text-muted small">{{ p.rutSocio }}</div>
                </td>
                <td class="d-none d-md-table-cell">{{ p.rutSocio }}</td>
                <td class="d-none d-lg-table-cell">
                  <span class="badge bg-light text-dark">{{ p.perfilPersona }}</span>
                </td>
                <td class="text-end fw-bold">{{ fmt(p.totalAccesos) }}</td>
                <td class="text-end text-success d-none d-sm-table-cell">{{ fmt(p.totalIngresos) }}</td>
                <td class="text-end text-danger d-none d-sm-table-cell">{{ fmt(p.totalSalidas) }}</td>
                <td class="d-none d-lg-table-cell">{{ p.ultimoAcceso }}</td>
                <td class="text-end">
                  <button class="btn btn-sm btn-outline-secondary" @click="openDetail(p)" title="Ver detalle">
                    <i class="fas fa-eye"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="!records.length">
                <td :colspan="columns.length + 1" class="text-center text-muted py-5">
                  <i class="fas fa-user-slash fa-2x d-block mb-2" style="opacity: 0.3;"></i>
                  No hay personas con accesos para los filtros seleccionados.
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="!loading && totalPages > 1" class="personas-pagination">
        <button class="btn btn-sm btn-outline-secondary" :disabled="page === 0" @click="goTo(0)" title="Primera">
          <i class="fas fa-angle-double-left"></i>
        </button>
        <button class="btn btn-sm btn-outline-secondary" :disabled="page === 0" @click="goTo(page - 1)" title="Anterior">
          <i class="fas fa-angle-left"></i>
        </button>
        <button
          v-for="p in pageWindow"
          :key="p"
          class="btn btn-sm"
          :class="p === page ? 'btn-success' : 'btn-outline-secondary'"
          @click="goTo(p)"
        >{{ p + 1 }}</button>
        <button class="btn btn-sm btn-outline-secondary" :disabled="page >= totalPages - 1" @click="goTo(page + 1)" title="Siguiente">
          <i class="fas fa-angle-right"></i>
        </button>
        <button class="btn btn-sm btn-outline-secondary" :disabled="page >= totalPages - 1" @click="goTo(totalPages - 1)" title="Última">
          <i class="fas fa-angle-double-right"></i>
        </button>
        <span class="personas-pagination-info">Página {{ page + 1 }} de {{ totalPages }}</span>
      </div>

      <p v-if="timezone && !loading" class="text-muted small text-end mt-3 mb-0">
        <i class="fas fa-clock"></i> Horarios en zona horaria: <strong>{{ timezone }}</strong>
      </p>
    </div>

    <!-- Modal de detalle -->
    <div v-if="detail" class="accesos-modal-overlay" @click.self="detail = null">
      <div class="accesos-modal">
        <div class="accesos-modal-header">
          <h6 class="mb-0"><i class="fas fa-user me-2"></i>{{ detail.nombrePersona }}</h6>
          <button class="btn-close-custom" @click="detail = null"><i class="fas fa-times"></i></button>
        </div>
        <div class="accesos-modal-body">
          <div class="detail-row">
            <span class="detail-label">RUT</span>
            <span class="detail-value">{{ detail.rutSocio }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Perfil</span>
            <span class="detail-value"><span class="badge bg-light text-dark">{{ detail.perfilPersona }}</span></span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Total accesos</span>
            <span class="detail-value">{{ fmt(detail.totalAccesos) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Ingresos / Salidas</span>
            <span class="detail-value">
              <span class="text-success">{{ fmt(detail.totalIngresos) }}</span>
              /
              <span class="text-danger">{{ fmt(detail.totalSalidas) }}</span>
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Primer acceso</span>
            <span class="detail-value">{{ detail.primerAcceso || '—' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Último acceso</span>
            <span class="detail-value">{{ detail.ultimoAcceso || '—' }}</span>
          </div>
          <div class="detail-block">
            <span class="detail-label">Sucursales</span>
            <div class="detail-chips">
              <span v-for="s in (detail.sucursales || [])" :key="s" class="chip">{{ s }}</span>
              <span v-if="!(detail.sucursales || []).length" class="text-muted">—</span>
            </div>
          </div>
          <div class="detail-block">
            <span class="detail-label">Planes</span>
            <div class="detail-chips">
              <span v-for="pl in (detail.planes || [])" :key="pl" class="chip">{{ pl }}</span>
              <span v-if="!(detail.planes || []).length" class="text-muted">—</span>
            </div>
          </div>
          <div class="detail-block">
            <span class="detail-label">Métodos de acceso</span>
            <div class="detail-chips">
              <span v-for="m in (detail.metodos || [])" :key="m" class="chip">{{ m }}</span>
              <span v-if="!(detail.metodos || []).length" class="text-muted">—</span>
            </div>
          </div>
        </div>
        <div class="accesos-modal-footer">
          <button class="btn btn-success" @click="detail = null">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const OP_PERSONAS = '/v1/access/get/gymaccess/people';

export default {
  name: 'AccesosPersonas',
  props: {
    // Snapshot de filtros comprometido por el padre (sin page/limit/sort).
    parameters: {
      type: Object,
      default: () => ({})
    },
    // Cambia cada vez que el padre aplica/limpia filtros: dispara recarga.
    paramsVersion: {
      type: Number,
      default: 0
    },
    // Indica si la pestaña Personas está activa (carga perezosa).
    active: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      records: [],
      totalRecords: 0,
      page: 0,
      limit: 20,
      limitOptions: [10, 20, 50, 100],
      sortField: 'totalAccesos',
      sortDirection: 'desc',
      timezone: '',
      loading: false,
      loadedVersion: -1,
      detail: null,
      columns: [
        { key: 'persona', label: 'Persona', sortField: 'nombrePersona' },
        { key: 'rut', label: 'RUT', sortField: 'rutSocio', thClass: 'd-none d-md-table-cell' },
        { key: 'perfil', label: 'Perfil', sortField: null, thClass: 'd-none d-lg-table-cell' },
        { key: 'total', label: 'Accesos', sortField: 'totalAccesos', thClass: 'text-end' },
        { key: 'ingresos', label: 'Ingresos', sortField: 'totalIngresos', thClass: 'text-end d-none d-sm-table-cell' },
        { key: 'salidas', label: 'Salidas', sortField: 'totalSalidas', thClass: 'text-end d-none d-sm-table-cell' },
        { key: 'ultimo', label: 'Último acceso', sortField: 'ultimoAcceso', thClass: 'd-none d-lg-table-cell' }
      ]
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalRecords / this.limit));
    },
    pageWindow() {
      const total = this.totalPages;
      const maxButtons = 5;
      let start = Math.max(0, this.page - 2);
      let end = Math.min(total, start + maxButtons);
      start = Math.max(0, end - maxButtons);
      const arr = [];
      for (let i = start; i < end; i++) arr.push(i);
      return arr;
    }
  },
  watch: {
    active(val) {
      if (val) this.refreshIfStale();
    },
    paramsVersion() {
      if (this.active) {
        this.page = 0;
        this.fetchPage();
      }
    }
  },
  mounted() {
    if (this.active) this.refreshIfStale();
  },
  methods: {
    fmt(n) {
      const num = Number(n);
      return isNaN(num) ? (n || 0) : num.toLocaleString('es-CL');
    },
    sortIcon(field) {
      if (this.sortField !== field) return 'fas fa-sort';
      return this.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },

    refreshIfStale() {
      if (this.loadedVersion !== this.paramsVersion) {
        this.page = 0;
        this.fetchPage();
      }
    },

    toggleSort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'desc';
      }
      this.page = 0;
      this.fetchPage();
    },

    onLimitChange() {
      this.page = 0;
      this.fetchPage();
    },

    goTo(p) {
      if (p < 0 || p >= this.totalPages || p === this.page) return;
      this.page = p;
      this.fetchPage();
    },

    openDetail(person) {
      this.detail = person;
    },

    async fetchPage() {
      this.loading = true;
      try {
        const Parameters = {
          ...this.parameters,
          page: this.page,
          limit: this.limit,
          sortField: this.sortField,
          sortDirection: this.sortDirection
        };
        const response = await this.$CentralGymOPS_API.post('', {
          Header: { Operacion: OP_PERSONAS },
          Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
          Parameters,
          Body: {}
        });
        const result = (response.data && response.data.result) || {};
        this.records = result.people || [];
        this.totalRecords = result.totalRecords || 0;
        if (typeof result.page === 'number') this.page = result.page;
        if (typeof result.limit === 'number') this.limit = result.limit;
        this.timezone = result.timezone || '';
        this.loadedVersion = this.paramsVersion;
      } catch (error) {
        this.records = [];
        this.totalRecords = 0;
        this.$logger.error('Error obteniendo personas con acceso:', error);
        this.handleRequestError(error);
      } finally {
        this.loading = false;
      }
    },

    handleRequestError(error) {
      const status = error && error.response && error.response.status;
      if (status === 401) return; // gestionado por el interceptor de axios
      let message = 'No se pudo obtener el resumen por persona.';
      const data = error && error.response && error.response.data;
      if (data && data.error) {
        try {
          const parsed = typeof data.error === 'string' ? JSON.parse(data.error) : data.error;
          if (parsed && parsed.message) message = parsed.message;
        } catch (e) {
          if (typeof data.error === 'string') message = data.error;
        }
      }
      this.$swal.fire({
        icon: status === 400 ? 'warning' : 'error',
        title: status === 400 ? 'Filtros inválidos' : 'Error',
        text: message,
        confirmButtonColor: '#6bd098'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.chart-card {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.personas-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f0f0f0;
}

.personas-total {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #66615b;

  i { color: #6bd098; }
}

.personas-limit {
  display: flex;
  align-items: center;

  label {
    font-size: 13px;
    color: #9a9a9a;
  }

  .form-select {
    width: auto;
    display: inline-block;
  }
}

.personas-table {
  thead th {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #9a9a9a;
    border-bottom: 2px solid #ececec;
    white-space: nowrap;

    &.sortable {
      cursor: pointer;
      user-select: none;

      &:hover { color: #66615b; }
    }
  }

  .sort-icon {
    margin-left: 4px;
    font-size: 11px;
    opacity: 0.6;
  }

  tbody td {
    vertical-align: middle;
  }
}

.personas-pagination {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;

  .personas-pagination-info {
    margin-left: 10px;
    font-size: 13px;
    color: #9a9a9a;
  }
}

/* ===== Modal de detalle (overlay propio, sin dependencia de Bootstrap JS) ===== */
.accesos-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
  padding: 16px;
}

.accesos-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
}

.accesos-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  color: #66615b;
}

.btn-close-custom {
  border: none;
  background: transparent;
  color: #9a9a9a;
  font-size: 18px;
  cursor: pointer;
  line-height: 1;

  &:hover { color: #66615b; }
}

.accesos-modal-body {
  padding: 8px 20px;
  overflow-y: auto;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid #f4f4f4;
}

.detail-block {
  padding: 10px 0;
  border-bottom: 1px solid #f4f4f4;

  &:last-child { border-bottom: none; }
}

.detail-label {
  font-size: 13px;
  color: #9a9a9a;
  flex-shrink: 0;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #2c2c2c;
  text-align: right;
  word-break: break-word;
}

.detail-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.chip {
  display: inline-block;
  background: #f4f4f4;
  color: #66615b;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
}

.accesos-modal-footer {
  padding: 14px 20px;
  border-top: 1px solid #eee;
  text-align: right;
}
</style>
